"use client";

import * as stylex from "@stylexjs/stylex";
import { CloudRain, FileText } from "lucide-react";
import { useRef } from "react";
import { type Breakpoint, type LayoutSize, layoutSize } from "@/domain/breakpoint";
import { type EventStatus, eventStatus } from "@/domain/eventStatus";
import type { AccountSettings, CalendarEvent, Tag, TagColor, Weather, WeatherIcon } from "@/domain/schemas";
import { eventColor, whoLabel } from "@/domain/tags";
import { displayTemp } from "@/domain/temperature";
import { atMinute, type Clock, formatClock, formatDuration, formatHour, rangeParts, timeParts } from "@/domain/time";
import { type FreeSlot, grownHeight, hourWeather, type MoreTile, type PlacedPlan, type PlacedStrip, type PlanCard, planCard, planRain, type TimelineLayout, type TimelineOptions, timelineLayout } from "@/domain/timeline";
import { type HourlyPoint, hourlyByHour } from "@/domain/weather";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Icon } from "./Icon";
import { PersonDot } from "./PersonDot";
import { press } from "./press";
import { type BoxSize, useBoxSize } from "./useBoxSize";
import { weatherIcons } from "./weatherIcons";
export type DayTimelineLabels = {
  everyone: string;
  rain: (percent: number) => string;
  until: (time: string) => string;
  free: (everyone: boolean, length: string) => string;
  freeName: (range: string) => string;
  planName: (title: string, range: string) => string;
  more: string;
  moreName: (count: number, range: string, opensCalendar: boolean) => string;
  longPlans: string;
  icons: Record<WeatherIcon, string>;
};
export type TimelineWeather = {
  hourly: Weather["hourly"];
  unit: AccountSettings["tempUnit"];
};
export type TimelineFree = "everyone" | "some" | "none";
type DayTimelineProps = {
  plans: readonly CalendarEvent[];
  tags: readonly Tag[];
  day: string;
  now: Date;
  tz: string;
  clock: Clock;
  variant: "calendar" | "today";
  breakpoint: Breakpoint;
  span?: "day" | "24h";
  free: TimelineFree;
  weather?: TimelineWeather;
  firstHour?: number;
  lastHour?: number;
  grow?: boolean;
  nowDot?: boolean;
  labels: DayTimelineLabels;
  onOpenEvent: (event: CalendarEvent) => void;
  onOpenMore: (earliest: CalendarEvent) => void;
  onAddEvent: (slot: {
    start: Date;
    end: Date;
  }) => void;
  xstyle?: stylex.StyleXStyles;
};
type TypeScale = {
  title: number;
  time: number;
  timeLine: number;
  detail: number;
  hour: number;
  oneLine: number;
};
type Variant = DayTimelineProps["variant"];
type Rules = Pick<TimelineOptions, "minColumn" | "minHourHeight" | "longStrips">;
type Fill = TagColor | "soft" | "past";
type Shared = {
  tags: readonly Tag[];
  now: Date;
  tz: string;
  clock: Clock;
  labels: DayTimelineLabels;
  scale: TypeScale;
};
const FALLBACK_SIZE: BoxSize = {
  width: 640,
  height: 640
};
const INSET = 12;
const PASSED_FACTOR = {
  calendar: 0.35,
  today: 0.3
} as const;
const MIN_PLAN_HEIGHT: Record<LayoutSize, number> = {
  large: 48,
  small: 40
};
const RULES: Record<Variant, Record<LayoutSize, Rules>> = {
  calendar: {
    large: {
      minColumn: 200,
      minHourHeight: 26,
      longStrips: true
    },
    small: {
      minColumn: 0,
      minHourHeight: 0,
      longStrips: false
    }
  },
  today: {
    large: {
      minColumn: 120,
      minHourHeight: 0,
      longStrips: true
    },
    small: {
      minColumn: 120,
      minHourHeight: 0,
      longStrips: true
    }
  }
};
const GUTTERS: Record<Variant, Record<LayoutSize, number>> = {
  calendar: {
    large: 96,
    small: 52
  },
  today: {
    large: 80,
    small: 52
  }
};
const BASE_TYPE: TypeScale = {
  title: 16,
  time: 14,
  timeLine: 18,
  detail: 15,
  hour: 14,
  oneLine: 17
};
const TYPE: Record<Variant, Record<Breakpoint, TypeScale>> = {
  calendar: {
    large: {
      ...BASE_TYPE,
      title: 22
    },
    normal: {
      ...BASE_TYPE,
      title: 22
    },
    small: BASE_TYPE
  },
  today: {
    large: {
      title: 20,
      time: 16,
      timeLine: 20,
      detail: 16,
      hour: 16,
      oneLine: 18
    },
    normal: {
      ...BASE_TYPE,
      title: 18
    },
    small: BASE_TYPE
  }
};
const GROW_START = 536;
const SLOT_INSET = 2;
const SLOT_GAP = 8;
const STRIP_WIDTH = 44;
const SLOT_LABEL = 28;
const _temp = {
  kVAEAm: "x10l6tqk",
  kkqhue: "x17y0mx6",
  k87sOh: "x1tk7jg1",
  "$$css": true
};
const _temp2 = {
  kVAEAm: "x10l6tqk",
  kkqhue: "x17y0mx6",
  k3aq6I: "x1v49fql",
  k1xSpc: "x78zum5",
  kGNEyG: "x1cy8zhl",
  kfzvcC: "x47corl",
  "$$css": true
};
const _temp3 = {
  kmuXW: "x2lah0s",
  kB7OPa: "x9f619",
  kwRFfy: "x2vl965",
  k1xSpc: "x78zum5",
  kXwgrk: "xdt5ytf",
  kOIVth: "x1jnr06f",
  "$$css": true
};
const _temp4 = {
  kVAEAm: "x10l6tqk",
  k87sOh: "x13vifvy",
  krVfgx: "x1ey2m1c",
  kCIrl2: "x3m8u43",
  "$$css": true
};
const _temp5 = {
  kVAEAm: "x10l6tqk",
  kbCHJM: "xu96u03",
  kCIrl2: "x19up5dg",
  "$$css": true
};
const _temp6 = {
  kVAEAm: "x10l6tqk",
  "$$css": true
};
const _temp7 = {
  kb6lSQ: "x1tup8yo",
  kLWn49: "x1u7k74",
  kmuXW: "x2lah0s",
  "$$css": true
};
const _temp8 = {
  kVAEAm: "x10l6tqk",
  kzqmXN: "x187nhsf",
  "$$css": true
};
const _temp9 = {
  kVAEAm: "x10l6tqk",
  kkqhue: "x17y0mx6",
  k3aq6I: "x1cb1t30",
  k1xSpc: "x78zum5",
  kGNEyG: "x6s0dn4",
  kfzvcC: "x47corl",
  "$$css": true
};
const _temp10 = {
  kmuXW: "x2lah0s",
  kB7OPa: "x9f619",
  k1xSpc: "x78zum5",
  kjj79g: "x13a6bvl",
  kwRFfy: "xf159sx",
  "$$css": true
};
const styles = {
  fontSize: (size: number) => [{
    kGuDYH: size != null ? "xdmh292" : size,
    $$css: true
  }, {
    "--x-fontSize": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size)
  }],
  grown: (height: number) => [{
    kAzted: height != null ? "x82snj4" : height,
    $$css: true
  }, {
    "--x-minHeight": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(height)
  }],
  root: {
    kVAEAm: "x1n2onr6",
    kzQI83: "x1iyjqo2",
    k7Eaqz: "xeuugli",
    kAzted: "x2lwn1j",
    kMwMTN: "x1a11ngh",
    $$css: true
  },
  inner: (height: number) => [_temp, {
    kZKoxP: height != null ? "x16ye13r" : height,
    $$css: true
  }, {
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(height)
  }],
  reflow: {
    k1ekBW: "x1xuz8iz",
    kIyJzY: "x1fmhw06",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  mark: (top: number) => [_temp2, {
    k87sOh: top != null ? "xjbys53" : top,
    $$css: true
  }, {
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(top)
  }],
  gutter: (width: number) => [_temp3, {
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }],
  hidden: {
    k33iCy: "xlshs6z",
    $$css: true
  },
  hourLabel: {
    kMv6JI: "xr0iwhn",
    kGuDYH: "xif65rj",
    kLWn49: "x1fc57z9",
    kb6lSQ: "xbzybt3",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  gutterWeather: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1jnr06f",
    kGuDYH: "x1jvydc1",
    k63SB2: "x1cf7cre",
    kLWn49: "xd4r4e8",
    kcqcaj: "xss6m8b",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  todayWeather: {
    kGuDYH: "x19d36u7",
    kLWn49: "x19v9tvf",
    $$css: true
  },
  hairline: {
    kzQI83: "x1iyjqo2",
    kZKoxP: "xjm9jq1",
    keoZOQ: "x1anpbxc",
    kWkggS: "x1wf3224",
    $$css: true
  },
  area: (left: number) => [_temp4, {
    kbCHJM: left != null ? "xgq9j65" : left,
    $$css: true
  }, {
    "--x-left": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left)
  }],
  slot: (top: number, height: number) => [_temp5, {
    k87sOh: top != null ? "xjbys53" : top,
    kZKoxP: height != null ? "x16ye13r" : height,
    $$css: true
  }, {
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(top),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(height)
  }],
  slotTile: {
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    k8WAf4: "xt970qd",
    kg3NbH: "xqin4a2",
    kVQacm: "xb3r6kr",
    kaIpWk: "x1fv5t7x",
    kMzoRj: "xdh2fpr",
    ksu8eU: "xbsl7fq",
    kVAM5u: "x1ky62hi",
    kWkggS: "xjbqb8w",
    $$css: true
  },
  slotLabel: {
    kMv6JI: "xr0iwhn",
    kGuDYH: "xif65rj",
    kb6lSQ: "xbzybt3",
    kP9fke: "xtvhhri",
    khDVqt: "xuxw1ft",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    $$css: true
  },
  plan: (top: number, height: number, left: string, width: string) => [_temp6, {
    k87sOh: top != null ? "xjbys53" : top,
    kZKoxP: height != null ? "x16ye13r" : height,
    kbCHJM: left != null ? "xgq9j65" : left,
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(top),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(height),
    "--x-left": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left),
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }],
  planButton: {
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    kB7OPa: "x9f619",
    kLKAdn: "xexx8yu",
    kZCmMZ: "x1c1uobl",
    kwRFfy: "xf159sx",
    kGO01o: "xjkvuk6",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    k1ekBW: "x1ftmoad",
    kIyJzY: "x1yvem80",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  card: {
    kZKoxP: "x5yr21d",
    kB7OPa: "x9f619",
    kaIpWk: "x1fv5t7x",
    k8WAf4: "x1vvogim",
    kg3NbH: "xaope02",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kjj79g: "x1nhvcw1",
    kOIVth: "x1jnr06f",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  oneLineCard: {
    k8WAf4: "xt970qd",
    kjj79g: "xl56j7k",
    $$css: true
  },
  soft: {
    kWkggS: "x1hueysq",
    $$css: true
  },
  past: {
    kWkggS: "xh540kj",
    kGVxlE: "x98nf4m",
    $$css: true
  },
  now: {
    kGVxlE: "xuamy0h",
    $$css: true
  },
  lilac: {
    kWkggS: "x1xnpmgp",
    $$css: true
  },
  sky: {
    kWkggS: "xv6g5m4",
    $$css: true
  },
  coral: {
    kWkggS: "x1aunxf9",
    $$css: true
  },
  mint: {
    kWkggS: "x1mxzhvz",
    $$css: true
  },
  sun: {
    kWkggS: "x11uy0h7",
    $$css: true
  },
  pink: {
    kWkggS: "x1y943kj",
    $$css: true
  },
  lime: {
    kWkggS: "xd0s4ba",
    $$css: true
  },
  rust: {
    kWkggS: "x14u7jty",
    $$css: true
  },
  glacier: {
    kWkggS: "x1sqgj6",
    $$css: true
  },
  amber: {
    kWkggS: "x1jvsd1a",
    $$css: true
  },
  sage: {
    kWkggS: "x1ibsdi1",
    $$css: true
  },
  ice: {
    kWkggS: "x5j0lpm",
    $$css: true
  },
  line: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    k7Eaqz: "xeuugli",
    kmuXW: "x2lah0s",
    $$css: true
  },
  oneLine: {
    kOIVth: "x1v2ro7d",
    $$css: true
  },
  firstLine: {
    kOIVth: "x167g77z",
    $$css: true
  },
  time: {
    kMv6JI: "xr0iwhn",
    kGuDYH: "xif65rj",
    kb6lSQ: "xbzybt3",
    khDVqt: "xuxw1ft",
    kmuXW: "x2lah0s",
    $$css: true
  },
  rangeTime: {
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    k7Eaqz: "xeuugli",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    $$css: true
  },
  ellipsis: {
    khDVqt: "xuxw1ft",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  oneLineTitle: {
    kzQI83: "x1iyjqo2",
    k7Eaqz: "xfvyar9",
    kGuDYH: "x19d36u7",
    kb6lSQ: "x1yrajzq",
    $$css: true
  },
  title: (size: number) => [_temp7, {
    kGuDYH: size != null ? "xdmh292" : size,
    $$css: true
  }, {
    "--x-fontSize": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size)
  }],
  twoLines: {
    k1xSpc: "x104kibb",
    kJFfOR: "x1h7i4cw",
    kgKLqz: "x1ua5tub",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  titleWeight: {
    k63SB2: "xfbjfs5",
    $$css: true
  },
  pastTitle: {
    k63SB2: "x1v94iar",
    $$css: true
  },
  dots: {
    k1xSpc: "x78zum5",
    kOIVth: "x1ed6fcf",
    kmuXW: "x2lah0s",
    $$css: true
  },
  meta: {
    kOIVth: "x167g77z",
    kGuDYH: "x1jvydc1",
    k63SB2: "x15i68ds",
    kLWn49: "xwn7fz2",
    $$css: true
  },
  note: {
    kOIVth: "x167g77z",
    kGuDYH: "x1jvydc1",
    k63SB2: "x1cf7cre",
    kLWn49: "xwn7fz2",
    $$css: true
  },
  icon: {
    k1xSpc: "x78zum5",
    kmuXW: "x2lah0s",
    $$css: true
  },
  rainBadge: {
    kmuXW: "x2lah0s",
    kaIpWk: "xqhrb7l",
    kMv6JI: "xr0iwhn",
    kGuDYH: "x4z9k3i",
    khDVqt: "xuxw1ft",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1jnr06f",
    k8WAf4: "xf4egzd",
    kZCmMZ: "xmzvs34",
    kwRFfy: "x2vl965",
    kb6lSQ: "xcheg63",
    kWkggS: "xh540kj",
    $$css: true
  },
  strip: (top: number, height: number, left: number) => [_temp8, {
    k87sOh: top != null ? "xjbys53" : top,
    kZKoxP: height != null ? "x16ye13r" : height,
    kbCHJM: left != null ? "xgq9j65" : left,
    $$css: true
  }, {
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(top),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(height),
    "--x-left": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left)
  }],
  strips: {
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kMzoRj: "xc342km",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  stripButton: {
    kwRFfy: "xyri2b",
    $$css: true
  },
  stripCard: {
    k8WAf4: "x10dr8xq",
    kg3NbH: "xnjsko4",
    kGNEyG: "x6s0dn4",
    $$css: true
  },
  stripText: {
    kW8zPn: "x98t3bc",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x883omv",
    khDVqt: "xuxw1ft",
    kVQacm: "xb3r6kr",
    kskxy: "xmz0i5r",
    $$css: true
  },
  stripTitle: {
    kGuDYH: "x19d36u7",
    kb6lSQ: "x1yrajzq",
    $$css: true
  },
  moreCard: {
    kWkggS: "x1hueysq",
    kGVxlE: "x98nf4m",
    k8WAf4: "x1t0vds8",
    kg3NbH: "xnjsko4",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "x195vfkc",
    $$css: true
  },
  moreCount: {
    kGuDYH: "xosj86m",
    k63SB2: "xfbjfs5",
    kLWn49: "x1u7k74",
    kcqcaj: "xss6m8b",
    $$css: true
  },
  moreWord: {
    kMv6JI: "xr0iwhn",
    kGuDYH: "xif65rj",
    kb6lSQ: "xbzybt3",
    kP9fke: "xtvhhri",
    $$css: true
  },
  nowMarker: (top: number) => [_temp9, {
    k87sOh: top != null ? "xjbys53" : top,
    $$css: true
  }, {
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(top)
  }],
  nowGutter: (width: number) => [_temp10, {
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }],
  nowPill: {
    kaIpWk: "xqhrb7l",
    k8WAf4: "x12ulsxz",
    kg3NbH: "x1awh872",
    kMv6JI: "xr0iwhn",
    kGuDYH: "xif65rj",
    kb6lSQ: "xbzybt3",
    kWkggS: "xfenp3h",
    kMwMTN: "xnnvkky",
    $$css: true
  },
  nowDot: {
    kzqmXN: "x6jxa94",
    kZKoxP: "x1v9usgg",
    kaIpWk: "xqhrb7l",
    kWkggS: "x1i4uan3",
    k71WvV: "xrqqxx7",
    $$css: true
  },
  nowLine: {
    kzQI83: "x1iyjqo2",
    kZKoxP: "x36qwtl",
    kWkggS: "x1i4uan3",
    $$css: true
  }
};
export function DayTimeline({
  plans,
  tags,
  day,
  now,
  tz,
  clock,
  variant,
  breakpoint,
  span = "day",
  free,
  weather,
  firstHour,
  lastHour,
  grow = false,
  nowDot = false,
  labels,
  onOpenEvent,
  onOpenMore,
  onAddEvent,
  xstyle
}: DayTimelineProps) {
  const ref = useRef<HTMLDivElement>(null);
  const size = useBoxSize(ref) ?? FALLBACK_SIZE;
  const record = layoutSize(breakpoint);
  const gutter = GUTTERS[variant][record];
  const scale = TYPE[variant][breakpoint];
  const hours = weather ? hourlyByHour(weather.hourly, day, tz) : new Map<number, HourlyPoint>();
  const chips = variant === "calendar" && record === "large";
  const context = {
    day,
    now,
    tz
  };
  const options: TimelineOptions = {
    span,
    height: GROW_START,
    width: Math.max(0, size.width - gutter),
    passedFactor: PASSED_FACTOR[variant],
    minPlanHeight: MIN_PLAN_HEIGHT[record],
    firstHour,
    lastHour,
    nowDot,
    ...RULES[variant][record]
  };
  const minHeight = grow ? grownHeight(plans, context, options) : 0;
  const height = Math.max(minHeight, size.height - 2 * INSET);
  const layout = timelineLayout(plans, context, {
    ...options,
    height
  });
  const shared: Shared = {
    tags,
    now,
    tz,
    clock,
    labels,
    scale
  };
  const columnsWidth = options.width - layout.stripsWidth;
  const rain = chips ? rainByHour(hours) : new Map<number, number>();
  return <div ref={ref} {...stylex.props(styles.root, xstyle, grow && styles.grown(minHeight + 2 * INSET))}>
      <div {...stylex.props(styles.inner(height))}>
        <HourMarks layout={layout} gutter={gutter} hourSize={scale.hour} hours={hours} weather={weather} sparse={variant === "today"} clock={clock} labels={labels} />
        {layout.now && <div {...stylex.props(styles.nowMarker(layout.now.top))}>
            <span {...stylex.props(styles.nowGutter(gutter))}>
              {nowDot ? <span role="img" aria-label={timeParts(layout.now.minutes, clock).time} className="x6jxa94 x1v9usgg xqhrb7l x1i4uan3 xrqqxx7" /> : <span className="xqhrb7l x12ulsxz x1awh872 xr0iwhn xif65rj xbzybt3 xfenp3h xnnvkky">
                  {timeParts(layout.now.minutes, clock).time}
                </span>}
            </span>
            <span className="x1iyjqo2 x36qwtl x1i4uan3" />
          </div>}
        {layout.strips.length > 0 && <fieldset aria-label={labels.longPlans} {...stylex.props(styles.area(gutter), styles.strips)}>
            {layout.strips.map(strip => <StripTile key={strip.plan.id} strip={strip} shared={shared} onClick={() => onOpenEvent(strip.plan)} />)}
          </fieldset>}
        <div {...stylex.props(styles.area(gutter + layout.stripsWidth))}>
          {free !== "none" && layout.freeSlots.map(slot => <FreeSlotTile key={slot.start} slot={slot} everyone={free === "everyone"} clock={clock} labels={labels} onClick={() => onAddEvent(newEventAt(slot, day, tz))} />)}
          {layout.plans.map(placed => <PlanTile key={placed.plan.id} placed={placed} shared={shared} width={placed.width * columnsWidth} rain={planRain(placed.start, placed.end, rain, placed.width * columnsWidth)} onClick={() => onOpenEvent(placed.plan)} />)}
          {layout.more.map(tile => <MoreTileButton key={tile.first.id} tile={tile} opensCalendar={variant === "today"} clock={clock} labels={labels} onClick={() => onOpenMore(tile.first)} />)}
        </div>
      </div>
    </div>;
}
function HourMarks({
  layout,
  gutter,
  hourSize,
  hours,
  weather,
  sparse,
  clock,
  labels
}: {
  layout: TimelineLayout<CalendarEvent>;
  gutter: number;
  hourSize: number;
  hours: ReadonlyMap<number, HourlyPoint>;
  weather: TimelineWeather | undefined;
  sparse: boolean;
  clock: Clock;
  labels: DayTimelineLabels;
}) {
  const shown = weather ? hourWeather(layout, hours, sparse) : new Set<number>();
  return layout.marks.map(mark => {
    const point = hours.get(mark.hour);
    const showWeather = shown.has(mark.hour);
    return <div key={mark.hour} {...stylex.props(styles.mark(mark.top), styles.reflow)}>
        <div {...stylex.props(styles.gutter(gutter), !mark.label && styles.hidden)}>
          <span {...stylex.props(styles.hourLabel, styles.fontSize(hourSize))}>
            {formatHour(mark.hour % 24 * 60, clock)}
          </span>
          {showWeather && point && weather && <span {...{
          0: {
            className: "x78zum5 x6s0dn4 x1jnr06f x1jvydc1 x1cf7cre xd4r4e8 xss6m8b xuxw1ft"
          },
          1: {
            className: "x78zum5 x6s0dn4 x1jnr06f x1cf7cre xss6m8b xuxw1ft x19d36u7 x19v9tvf"
          }
        }[!!sparse << 0]}>
              <Icon icon={weatherIcons[point.icon]} size={sparse ? 20 : 16} strokeWidth={1.5} label={labels.icons[point.icon]} />
              {temperature(point.temp, weather.unit)}
            </span>}
        </div>
        <span {...{
        0: {
          className: "x1iyjqo2 xjm9jq1 x1anpbxc x1wf3224"
        },
        1: {
          className: "x1iyjqo2 xjm9jq1 x1anpbxc x1wf3224 xlshs6z"
        }
      }[!!!mark.line << 0]} />
      </div>;
  });
}
function FreeSlotTile({
  slot,
  everyone,
  clock,
  labels,
  onClick
}: {
  slot: FreeSlot;
  everyone: boolean;
  clock: Clock;
  labels: DayTimelineLabels;
  onClick: () => void;
}) {
  const height = slot.height - SLOT_GAP;
  return <button type="button" aria-label={labels.freeName(rangeText(slot.start, slot.end, clock))} onClick={onClick} {...stylex.props(styles.slot(slot.top + SLOT_INSET, height), styles.slotTile, styles.reflow, press.scale)}>
      {height >= SLOT_LABEL && <span className="xr0iwhn xif65rj xbzybt3 xtvhhri xuxw1ft xb3r6kr xlyipyv">
          {labels.free(everyone, formatDuration(slot.end - slot.start))}
        </span>}
    </button>;
}
function PlanTile({
  placed,
  shared,
  width,
  rain,
  onClick
}: {
  placed: PlacedPlan<CalendarEvent>;
  shared: Shared;
  width: number;
  rain: number;
  onClick: () => void;
}) {
  const {
    plan: event
  } = placed;
  const status = eventStatus(event, shared.now, shared.tz);
  const chip = status === "past" ? 0 : rain;
  const note = firstLine(event.notes);
  const {
    scale
  } = shared;
  const card = planCard({
    width,
    height: placed.height,
    titleSize: scale.title,
    timeLine: scale.timeLine,
    badge: chip > 0,
    note: note !== ""
  });
  const end = event.endAt === undefined ? undefined : placed.end;
  const title = <span {...stylex.props(card.oneLine ? [styles.ellipsis, styles.oneLineTitle, styles.fontSize(scale.oneLine)] : styles.title(scale.title), !card.oneLine && (card.titleLines === 2 ? styles.twoLines : styles.ellipsis), status === "past" ? styles.pastTitle : styles.titleWeight)}>
      {event.title}
    </span>;
  return <button type="button" aria-label={shared.labels.planName(event.title, rangeText(placed.start, end, shared.clock))} onClick={onClick} {...stylex.props(styles.plan(placed.top, placed.height, percent(placed.left), percent(placed.width)), styles.planButton, press.scale)}>
      <span {...stylex.props(styles.card, card.oneLine && styles.oneLineCard, styles[fillOf(event, status, shared.tags)], status === "now" && styles.now)}>
        {card.oneLine ? <span className="x78zum5 x6s0dn4 xeuugli x2lah0s x1v2ro7d">
            {card.time !== "none" && <span {...stylex.props(styles.time, styles.fontSize(scale.time))}>
                {timeText(placed, card, shared, end)}
              </span>}
            {title}
            <PersonDots event={event} tags={shared.tags} />
          </span> : <>
            <span className="x78zum5 x6s0dn4 xeuugli x2lah0s x167g77z">
              <span {...stylex.props(styles.time, styles.rangeTime, styles.fontSize(scale.time))}>
                {timeText(placed, card, shared, end)}
              </span>
              {chip > 0 && <span className="x2lah0s xqhrb7l xr0iwhn x4z9k3i xuxw1ft x78zum5 x6s0dn4 x1jnr06f xf4egzd xmzvs34 x2vl965 xcheg63 xh540kj">
                  <Icon icon={CloudRain} size={16} />
                  {shared.labels.rain(chip)}
                </span>}
            </span>
            {title}
            {card.meta && <span {...stylex.props(styles.line, styles.meta, styles.fontSize(scale.detail))}>
                <PersonDots event={event} tags={shared.tags} />
                <span className="xuxw1ft xb3r6kr xlyipyv xeuugli">{metaText(event, shared)}</span>
              </span>}
            {card.note && <span {...stylex.props(styles.line, styles.note, styles.fontSize(scale.detail))}>
                <span className="x78zum5 x2lah0s">
                  <Icon icon={FileText} size={18} />
                </span>
                <span className="xuxw1ft xb3r6kr xlyipyv xeuugli">{note}</span>
              </span>}
          </>}
      </span>
    </button>;
}
function StripTile({
  strip,
  shared,
  onClick
}: {
  strip: PlacedStrip<CalendarEvent>;
  shared: Shared;
  onClick: () => void;
}) {
  const {
    plan: event
  } = strip;
  const status = eventStatus(event, shared.now, shared.tz);
  const end = event.endAt === undefined ? undefined : strip.end;
  return <button type="button" aria-label={shared.labels.planName(event.title, rangeText(strip.start, end, shared.clock))} onClick={onClick} {...stylex.props(styles.strip(strip.top, strip.height, strip.left), styles.planButton, styles.stripButton, press.scale)}>
      <span {...stylex.props(styles.card, styles.stripCard, styles[fillOf(event, status, shared.tags)], status === "now" && styles.now)}>
        <span className="x98t3bc x78zum5 x6s0dn4 x883omv xuxw1ft xb3r6kr xmz0i5r">
          <span {...{
          0: {
            className: "x19d36u7 x1yrajzq xfbjfs5"
          },
          1: {
            className: "x19d36u7 x1yrajzq x1v94iar"
          }
        }[!!(status === "past") << 0]}>
            {event.title}
          </span>
          <span className="xr0iwhn xif65rj xbzybt3 xuxw1ft x2lah0s">
            {rangeText(strip.start, strip.end, shared.clock)}
          </span>
        </span>
      </span>
    </button>;
}
function MoreTileButton({
  tile,
  opensCalendar,
  clock,
  labels,
  onClick
}: {
  tile: MoreTile<CalendarEvent>;
  opensCalendar: boolean;
  clock: Clock;
  labels: DayTimelineLabels;
  onClick: () => void;
}) {
  const name = labels.moreName(tile.count, rangeText(tile.start, tile.end, clock), opensCalendar);
  return <button type="button" aria-label={name} onClick={onClick} {...stylex.props(styles.plan(tile.top, tile.height, percent(tile.left), percent(1 - tile.left)), styles.planButton, press.scale)}>
      <span className="x5yr21d x9f619 x1fv5t7x x78zum5 xdt5ytf xb3r6kr x1hueysq x98nf4m x1t0vds8 xnjsko4 x6s0dn4 xl56j7k x195vfkc">
        <span className="xosj86m xfbjfs5 x1u7k74 xss6m8b">{`+${tile.count}`}</span>
        {tile.tall && <span className="xr0iwhn xif65rj xbzybt3 xtvhhri">{labels.more}</span>}
      </span>
    </button>;
}
function PersonDots({
  event,
  tags
}: {
  event: CalendarEvent;
  tags: readonly Tag[];
}) {
  if (event.everyone || event.tagIds.length < 2) return null;
  const on = tags.filter(tag => event.tagIds.includes(tag.id));
  return <span className="x78zum5 x1ed6fcf x2lah0s">
      {on.map(tag => <PersonDot key={tag.id} color={tag.color} size="sm" />)}
    </span>;
}
function rainByHour(hours: ReadonlyMap<number, HourlyPoint>): Map<number, number> {
  return new Map([...hours].map(([hour, point]) => [hour, point.precip ?? 0]));
}
function newEventAt(slot: FreeSlot, day: string, tz: string) {
  return {
    start: atMinute(day, slot.newEvent.start, tz),
    end: atMinute(day, slot.newEvent.end, tz)
  };
}
function rangeText(start: number, end: number | undefined, clock: Clock): string {
  if (end === undefined) return formatClock(start, clock);
  const {
    time,
    meridiem
  } = rangeParts(start, end, clock);
  return meridiem ? `${time} ${meridiem}` : time;
}
function timeText(placed: PlacedPlan<CalendarEvent>, card: PlanCard, {
  clock,
  labels
}: Shared, end: number | undefined): string {
  const format = (minutes: number) => card.oneLine ? timeParts(minutes, clock).time : formatClock(minutes, clock);
  if (placed.startsBefore) return labels.until(format(placed.end));
  return card.time === "range" ? rangeText(placed.start, end, clock) : format(placed.start);
}
function metaText(event: CalendarEvent, {
  tags,
  labels
}: Shared): string {
  const who = whoLabel(event, tags, labels.everyone);
  return [who, event.location].filter(Boolean).join(" · ");
}
function fillOf(event: CalendarEvent, status: EventStatus, tags: readonly Tag[]): Fill {
  return status === "past" ? "past" : eventColor(event, tags);
}
function firstLine(notes: string | undefined): string {
  return notes?.trim().split("\n")[0]?.trim() ?? "";
}
function percent(share: number): string {
  return `${share * 100}%`;
}
function temperature(fahrenheit: number, unit: TimelineWeather["unit"]): string {
  return `${displayTemp(fahrenheit, unit)}°`;
}